import React, { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import LazyImage from '@/components/base/lazy-image'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { teamRevealAnimation } from '@/components/sections/animations/team'
import Spacer from '@/components/sections/spacer'

import { WPImage } from '@/types/wp'

export type TeamProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    items: {
      image: WPImage
      title: string
      text: string
    }[]
  }>

const buildGridItems = (items: TeamProps['items']) => {
  if (!items) return []

  return items.map((item, itemIndex) => (
    <div
      key={itemIndex}
      className="col-span-2 translate-y-[40px] md:col-span-3"
      data-motion-item
    >
      <div className="relative aspect-[191/243] w-full overflow-hidden">
        <LazyImage
          src={item.image?.url}
          alt={item.image?.alt || ''}
          fill
          sizes="(max-width: 768px) 100vw, 33vw"
          className="object-cover"
        />
      </div>
      <div className="mt-spacing-sm flex flex-col items-end gap-y-spacing-xs">
        <Typography
          variant="large"
          component="h3"
          className="text-text-heading-light dark:text-text-heading-dark"
        >
          {item.title}
        </Typography>
        <Typography
          variant="regular"
          className="condensed text-text-primary-light dark:text-text-primary-dark"
        >
          {item.text}
        </Typography>
      </div>
    </div>
  ))
}

const Team = ({ items, ...props }: TeamProps) => {
  return items && items.length > 0 ? (
    <Motion animation={teamRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Spacer variant="xxl" />
          <Container>
            <Grid className="gap-y-spacing-md md:gap-y-spacing-3xl">
              {buildGridItems(items)}
            </Grid>
          </Container>
          <Spacer variant="xxl" />
        </Section>
      )}
    </Motion>
  ) : null
}

export default Team
